// LedConfigModal.jsx - LED configuration (unified premium layout)
import React, { useState } from 'react';
import { X, Lightbulb } from 'lucide-react';
import SearchableTagSelect from './SearchableTagSelect';

const DEFAULT_LED_CONFIG = {
  onFeedback:  { tagName: '', color: '#00ff00', label: 'ON' },
  offFeedback: { tagName: '', color: '#ff0000', label: 'OFF' },
};

const THEME = (dark) => dark ? {
  bg: '#0f1117', card: '#161922', text: '#e5e7eb', sub: '#7a8599',
  border: '#2a2f3d', accent: '#667eea', success: '#10b981',
} : {
  bg: '#ffffff', card: '#f9fafb', text: '#111827', sub: '#6b7280',
  border: '#e5e7eb', accent: '#667eea', success: '#10b981',
};

function TagRow({ label, tagName, color, onTagChange, onColorChange, availableTags, darkMode, t }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ fontSize: '11px', fontWeight: '600', color: t.sub, marginBottom: '4px' }}>{label}</div>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <SearchableTagSelect value={tagName} onChange={onTagChange} tags={availableTags} placeholder="Select tag…" darkMode={darkMode} />
        </div>
        <div style={{ position: 'relative', width: '28px', height: '28px', flexShrink: 0 }}>
          <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: color, border: '2px solid rgba(255,255,255,0.1)', boxShadow: `0 0 8px ${color}44` }} />
          <input type="color" value={color} onChange={e => onColorChange(e.target.value)}
            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }} />
        </div>
      </div>
    </div>
  );
}

function Toggle({ value, onChange, label, t }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '8px' }}>
      <div onClick={() => onChange(!value)} style={{
        width: '32px', height: '17px', borderRadius: '9px', position: 'relative', cursor: 'pointer',
        background: value ? t.accent : t.border, transition: 'background .2s',
      }}>
        <div style={{ position: 'absolute', top: '2px', left: value ? '17px' : '2px', width: '13px', height: '13px',
          borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
      </div>
      <span style={{ fontSize: '11px', color: t.sub }}>{label}</span>
    </label>
  );
}

export default function LedConfigModal({ component, availableTags, onSave, onClose, darkMode }) {
  const existing = component?.ledConfig || {};
  const merge = (key) => ({ ...DEFAULT_LED_CONFIG[key], ...(existing[key] || {}) });
  const t = THEME(darkMode);

  const [label, setLabel]               = useState(component?.label || '');
  const [defaultColor, setDefaultColor] = useState(component?.defaultColor || '#64748b');
  const [showStatusLabel, setShowStatusLabel] = useState(component?.showStatusLabel !== false);
  const [onFeedback, setOnFeedback]     = useState(merge('onFeedback'));
  const [offFeedback, setOffFeedback]   = useState(merge('offFeedback'));

  const handleSave = () => {
    onSave({
      ...component, label, defaultColor, showStatusLabel,
      ledConfig: { onFeedback, offFeedback }
    });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, backdropFilter: 'blur(4px)' }}>
      <div style={{ background: t.bg, borderRadius: '14px', width: '100%', maxWidth: '420px', maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: `0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px ${t.border}`, border: `1px solid ${t.border}` }}>

        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: `${t.accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Lightbulb size={16} color={t.accent} />
            </div>
            <span style={{ fontSize: '15px', fontWeight: '700', color: t.text }}>Configure LED</span>
          </div>
          <button onClick={onClose} style={{ width: '28px', height: '28px', borderRadius: '8px', border: `1px solid ${t.border}`, background: 'rgba(255,255,255,0.05)', color: t.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', lineHeight: 1, padding: 0 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          {/* Feedback */}
          <div style={{ background: t.card, borderRadius: '10px', padding: '14px', marginBottom: '14px', border: `1px solid ${t.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', paddingBottom: '8px', borderBottom: `1px solid ${t.border}` }}>
              <Lightbulb size={12} color={t.success} />
              <span style={{ fontSize: '11px', fontWeight: '700', color: t.text, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Feedback</span>
            </div>
            <TagRow label="On Feedback" tagName={onFeedback.tagName} color={onFeedback.color}
              onTagChange={v => setOnFeedback(p => ({ ...p, tagName: v }))} onColorChange={v => setOnFeedback(p => ({ ...p, color: v }))}
              availableTags={availableTags} darkMode={darkMode} t={t} />
            <TagRow label="Off Feedback" tagName={offFeedback.tagName} color={offFeedback.color}
              onTagChange={v => setOffFeedback(p => ({ ...p, tagName: v }))} onColorChange={v => setOffFeedback(p => ({ ...p, color: v }))}
              availableTags={availableTags} darkMode={darkMode} t={t} />
          </div>

          {/* Appearance */}
          <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-end', marginBottom: '12px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '10px', fontWeight: '600', color: t.sub, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Label</div>
              <input type="text" value={label} onChange={e => setLabel(e.target.value)} placeholder="Enter label…"
                style={{ width: '100%', padding: '8px 10px', background: t.card, border: `1px solid ${t.border}`, borderRadius: '8px', color: t.text, fontSize: '12px', boxSizing: 'border-box', outline: 'none' }} />
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', paddingBottom: '2px' }}>
              <div style={{ position: 'relative', width: '28px', height: '28px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: defaultColor, border: '2px solid rgba(255,255,255,0.1)', boxShadow: `0 0 8px ${defaultColor}44` }} />
                <input type="color" value={defaultColor} onChange={e => setDefaultColor(e.target.value)}
                  style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }} />
              </div>
              <input type="text" value={defaultColor} onChange={e => setDefaultColor(e.target.value)}
                style={{ width: '72px', padding: '7px 8px', background: t.card, border: `1px solid ${t.border}`, borderRadius: '8px', color: t.text, fontSize: '11px', fontFamily: 'monospace', boxSizing: 'border-box', outline: 'none' }} />
            </div>
          </div>
          <Toggle value={showStatusLabel} onChange={setShowStatusLabel} label="Status Label" t={t} />
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${t.border}`, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} style={{ padding: '8px 20px', background: 'transparent', border: `1px solid ${t.border}`, borderRadius: '8px', color: t.sub, cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Cancel</button>
          <button onClick={handleSave} style={{ padding: '8px 24px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: '600', boxShadow: '0 2px 8px rgba(102,126,234,0.4)' }}>Save</button>
        </div>
      </div>
    </div>
  );
}
